import { AxiosInstance } from "axios";
import { makeAutoObservable } from "mobx";
import AlertMessageStore, { AlertMessage } from "@/stores/AlertMessageStore";
import { download, DownloadResult, DownloadService } from "@/api/DownloadService";
import { compact, identity } from "lodash";
import {
  emailValidator,
  failure,
  isFailure,
  isOk,
  isPending,
  ok,
  pending,
  RenderedDecisionRow,
  Result,
  VotingEligibilityOfShares,
} from "@/common/common";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { LoggedOnUserInfoDTO } from "@/types/UserInfo";
import { TabButtonProps } from "@/components-v2/Tabs";
import { NumberOfShares } from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { percentOf } from "@/utils/numbers";
import { toDate } from "@/utils/Dates";
import { AccessType } from "@/types/enums/AccessType";
import { Decision } from "@/dbot/project/projectDomain";
import {
  hasNonPdfAttachment,
  MeetingAuthStatus,
} from "@/noticeForBoardMember/noticeForBoardMemberDomain";
import { ElectronicGeneralMeeting, GeneralMeetingStatus } from "./electronicGeneralMeetingDomain";
import { Language } from "@/i18n/languages";
import {
  CompanyRoleIdWithName,
  emptyFreetext,
  GeneralMeetingIndividualVote,
  GeneralMeetingParticipation,
  IndividualVote,
  voteLabelUppercase,
} from "@/dbot/common/commonDomain";
import Big from "big.js";
import { validateName } from "@/utils/Roles";
import { TFunction, TMessage, TMessageParam } from "@/i18n/tt";
import { PreviewState } from "@/meetings/meetingsDomain";
import { History } from "history";
import { EventDispatcher, showAlert, showError, showGeneralError } from "@/events/eventsDomain";
import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { downloadFile, HttpStatus, isHttpError } from "@/api";
import { formatDateTime } from "@/i18n/dateFormat";
import { CommentDTO } from "@/comments/commentsDomain";
import { DownloadAsPdfButtonProps } from "@/common/commonComponents";
import { DocumentType } from "@/common/documents";
import { ShareType, ShareTypeCode } from "@/common/share";
import { PieChartDatum } from "@/components-v2/Sparkline";

export const attendanceItem: AgendaItemType = { type: "attendance" };
export const meetingChairItem: AgendaItemType = { type: "meetingChair" };
export const approveAgendaItem: AgendaItemType = { type: "approveAgenda" };
export const matterForVoting = (matter: MatterForVoting): AgendaItemType => ({
  type: "matter",
  matter,
});

export type AgendaItemType =
  | { type: "attendance" }
  | { type: "meetingChair" }
  | { type: "approveAgenda" }
  | { type: "matter"; matter: MatterForVoting };

export enum NoticeActiveView {
  notice = "notice",
  poa = "poa",
  receivedPoA = "receivedPoA",
  minutes = "minutes",
  meeting = "meeting",
  powerOfAttorneyRecipientView = "powerOfAttorneyRecipientView",
}

export const toNoticeActiveView = (s: string | undefined): NoticeActiveView | undefined =>
  s && s in NoticeActiveView ? (s as NoticeActiveView) : undefined;

export type NoticeError = {
  authStatus?: MeetingAuthStatus;
  companyName?: string;
  companyLogo?: string;
};

export class NoticeForShareholderInitializerController {
  http: AxiosInstance;
  result: Result<NoticeForShareholderController, NoticeError> = pending();
  messageStore: AlertMessageStore;
  readonly onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void;
  meetingPassword = new StringFieldModel();

  constructor(
    http: AxiosInstance,
    messageStore: AlertMessageStore,
    readonly dispatch: EventDispatcher,
    onSetUserInfo: (userInfo: LoggedOnUserInfoDTO) => void,
  ) {
    this.http = http;
    this.messageStore = messageStore;
    this.onSetUserInfo = onSetUserInfo;
    makeAutoObservable(this, { http: false, onSetUserInfo: false }, { autoBind: true });
  }

  fetchNoticeForShareholder(noticeToGeneralMeetingId: string) {
    this.setResult(pending());
    const password = this.meetingPassword.inputValue;
    this.http
      .get<GetNoticeForShareholderResponse>(
        `/shareholder-view/notice-general-meeting/${noticeToGeneralMeetingId}`,
        password ? { auth: { username: "", password: password } } : undefined,
      )
      .then((response) => {
        this.setResult(
          ok(
            new NoticeForShareholderController(
              response.data,
              new DownloadService(this.http),
              this.http,
              this.dispatch,
              `/api/shareholder-view/notice-general-meeting/${response.data.noticeForShareholderId}/download-pdf`,
            ),
          ),
        );

        this.meetingPassword.clear();
      })
      .catch((err) => {
        if (isHttpError(HttpStatus.UNAUTHORIZED, err)) {
          const e = err.response?.data as NoticeError;
          this.setResult(
            isFailure(this.result) ? failure({ ...this.result.error, ...e }) : failure(e),
          );
          this.meetingPassword.clear();
        } else {
          this.setResult(failure({}));
        }
      });
  }

  setResult(result: Result<NoticeForShareholderController, NoticeError>) {
    this.result = result;
    if (isOk(this.result)) {
      this.onSetUserInfo(this.result.data.response.userInfo);
    }
  }
}

export interface GivePowerOfAttorneyModel {
  companyName: string;
  poaRecipientRoleModel: RadioFieldModel<PoARecipientRole | undefined>;
  poaOtherRecipientModel: PoAOtherRecipientModel;
  isSendPoADisabled: boolean;
  sendPoA: () => void;
  revokePoA: () => void;
  givenPowerOfAttorney?: boolean;
  givenPowerOfAttorneyTo?: string;
  poaRecipientName: string;
  isShareholderCompany: boolean;
  shareholderName: string;
  contactPersonName?: string;
  isAdminPreview: boolean;
  shareholderOptions: PowerOfAttorneyShareholderOption[];
  shareholderRecipientModel?: PowerOfAttorneyShareholderOption;
  givePoAResult?: Result<TMessage, TMessage>;
}

export class PoAOtherRecipientModel {
  name: StringFieldModel;
  email: StringFieldModel;
  shareholderOptions: PowerOfAttorneyShareholderOption[];
  selectedSuggestion?: PowerOfAttorneyShareholderOption;

  constructor(
    shareholderOptions: PowerOfAttorneyShareholderOption[],
    name?: string,
    email?: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.name = new StringFieldModel({
      required: true,
      value: name,
      validator: validateName,
    });
    this.email = new StringFieldModel({
      required: true,
      value: email,
      validator: emailValidator,
    });
    this.shareholderOptions = shareholderOptions;
  }

  setSelectedSuggestion(value: PowerOfAttorneyShareholderOption) {
    this.selectedSuggestion = value;
  }

  get suggestions(): PowerOfAttorneyShareholderOption[] {
    const suggestions = this.shareholderOptions.filter(
      (d) => d.email?.toLowerCase() === this.email.inputValue.trim().toLowerCase(),
    );

    if (suggestions.length === 1) {
      this.selectedSuggestion = suggestions[0];
    } else {
      this.selectedSuggestion = undefined;
    }
    return suggestions;
  }
  get isSendOtherDisabled(): boolean {
    return (
      this.name.isEmpty ||
      this.email.isEmpty ||
      !this.name.isValid ||
      !this.email.isValid ||
      !this.isSuggestionsValid
    );
  }

  get isSuggestionsValid() {
    return (
      this.suggestions.length === 0 ||
      (this.suggestions.length === 1 && this.selectedSuggestion === this.suggestions[0]) ||
      (this.suggestions.length > 1 &&
        this.suggestions.some((d) => d.id === this.selectedSuggestion?.id))
    );
  }

  validate() {
    this.name.validate();
    this.email.validate();
  }

  get isValid() {
    return this.name.isValid && this.email.isValid && this.isSuggestionsValid;
  }
}

export type RequestPageReload = {
  title: TMessage;
  reason: TMessage;
};

export class NoticeForShareholderController implements GivePowerOfAttorneyModel {
  http: AxiosInstance;
  response: GetNoticeForShareholderResponse;
  activeView: NoticeActiveView;
  previewState?: PreviewState;
  downloadService: DownloadService;
  noticeDownloadResult?: Result;
  showDownloadNonPdfWarning = false;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  poaRecipientRoleModel: RadioFieldModel<PoARecipientRole | undefined>;
  poaOtherRecipientModel: PoAOtherRecipientModel;
  shareholderOptions: PowerOfAttorneyShareholderOption[];
  shareholderRecipientModel?: PowerOfAttorneyShareholderOption;
  givePoAResult?: Result<TMessage, TMessage>;
  selectedAttendance: OptionFieldModel<AttendanceOption>;
  requestPageReload?: RequestPageReload;

  downloadNoticeAndAttachmentsUrl: string;

  constructor(
    noticeForShareholderResponse: GetNoticeForShareholderResponse,
    downloadService: DownloadService,
    http: AxiosInstance,
    readonly dispatch: EventDispatcher,
    readonly downloadUrl: string,
    readonly isAdminPreview: boolean = false,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.http = http;
    this.downloadService = downloadService;
    this.response = noticeForShareholderResponse;
    this.activeView = NoticeActiveView.notice;
    this.selectedAttendance = new OptionFieldModel({
      selectedIds: selectedAttendances(
        prepareAttendanceData(this.response),
        this.response.ownNotices,
        this.response,
      ),
      options: selectedAttendanceOptions(
        this.response.receivedPowerOfAttorneys,
        this.response.ownNotices,
        this.response,
      ),
      idFn: (x) => x.shareholderId.toString(),
      labelFn: (x) => x.label,
    });

    this.poaRecipientRoleModel = new RadioFieldModel<PoARecipientRole | undefined>({
      value: noticeForShareholderResponse.poaRecipientRole,
      options:
        noticeForShareholderResponse.ceoName === ""
          ? Object.values(PoARecipientRole).filter((role) => role !== PoARecipientRole.ceo)
          : Object.values(PoARecipientRole),
      idFn: identity,
      labelFn: this.getLabelForRole,
      required: true,
    });
    this.shareholderOptions = noticeForShareholderResponse.shareholders.filter(
      (d) => d.id !== noticeForShareholderResponse.shareholderId,
    );
    this.shareholderRecipientModel = this.shareholderOptions.find(
      (d) => d.id === noticeForShareholderResponse.poaRecipientShareholderId,
    );
    const isOther = this.response.poaRecipientRole === PoARecipientRole.other;
    this.poaOtherRecipientModel = new PoAOtherRecipientModel(
      this.shareholderOptions,
      isOther ? this.response.poaRecipientName : "",
      isOther ? this.response.poaRecipientEmail : "",
    );
    this.downloadNoticeAndAttachmentsUrl = isAdminPreview
      ? `/shareholder-view/preview-meeting/download-notice-and-attachments/${this.response.noticeDocumentId}`
      : `/shareholder-view/notice-general-meeting/${this.response.noticeForShareholderId}/download-notice-and-attachments/${this.response.noticeDocumentId}`;
  }

  updateSelectedAttendances() {
    this.selectedAttendance = new OptionFieldModel({
      selectedIds: selectedAttendances(
        prepareAttendanceData(this.response),
        this.response.ownNotices,
        this.response,
      ),
      options: selectedAttendanceOptions(
        this.response.receivedPowerOfAttorneys,
        this.response.ownNotices,
        this.response,
      ),
      idFn: (x) => x.shareholderId.toString(),
      labelFn: (x) => x.label,
    });
  }

  getLabelForRole(value: PoARecipientRole | undefined): TMessage {
    if (!value) {
      return { translated: "" };
    }
    switch (value) {
      case PoARecipientRole.ceo:
        return { key: "ceo-with-name", params: { name: this.ceoName } };
      case PoARecipientRole.chairperson:
        return {
          key: "chairperson-with-name",
          params: { name: this.chairpersonName },
        };
      case PoARecipientRole.shareholder:
        return {
          key: "noticeForShareholder.poaShareholder",
        };
      default:
        return { key: "noticeForShareholder.otherNamedRecipient" };
    }
  }

  get companyName(): string {
    return this.response.companyName;
  }

  get isLiveGeneralMeeting() {
    if (this.response.conductElectronicGeneralMeeting) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      return toDate(this.response.meetingEndDate) > now;
    } else {
      return false;
    }
  }

  get isShareholderCompany() {
    return this.response.isCompany;
  }

  get shareholderName() {
    return this.response.recipientName;
  }

  get contactPersonName() {
    return this.response.contactPersonName;
  }

  get showNoticeForShareholderView(): boolean {
    return this.activeView === NoticeActiveView.notice;
  }

  get chairpersonName(): string {
    return this.response.chairpersonName;
  }

  get givenPowerOfAttorneyTo(): string | undefined {
    return this.response.poaRecipientName;
  }

  get hasReceivedPowerOfAttorney(): boolean {
    return this.response.receivedPowerOfAttorneys.length > 0;
  }

  get givenPowerOfAttorney(): boolean {
    return this.response.poaRecipientName !== undefined;
  }

  get ceoName(): string {
    return this.response.ceoName;
  }

  setNoticeForShareholderView() {
    this.activeView = NoticeActiveView.notice;
  }

  get showGivePowerOfAttorneyView(): boolean {
    return this.activeView === NoticeActiveView.poa;
  }

  get showNamedPersonInput(): boolean {
    return this.poaRecipientRoleModel.value === PoARecipientRole.other;
  }

  get isSendPoADisabled(): boolean {
    const isOther =
      this.poaRecipientRoleModel.value === PoARecipientRole.other &&
      this.poaOtherRecipientModel.isSendOtherDisabled;

    const isShareholder =
      this.poaRecipientRoleModel.value === PoARecipientRole.shareholder &&
      this.shareholderRecipientModel === undefined;

    const isNotChanged =
      this.response.poaRecipientName === this.poaRecipientName &&
      this.response.poaRecipientRole === this.poaRecipientRoleModel.value;

    return (
      isOther ||
      isShareholder ||
      isNotChanged ||
      this.poaRecipientRoleModel.value === undefined ||
      this.isAdminPreview
    );
  }

  setGivePowerOfAttorneyView() {
    this.activeView = NoticeActiveView.poa;
  }

  validate() {
    this.poaRecipientRoleModel.validate();
    this.poaOtherRecipientModel.validate();
  }

  get isValid() {
    const isShareholderRecipientValid =
      this.poaRecipientRoleModel.value === PoARecipientRole.shareholder &&
      this.shareholderRecipientModel !== undefined;
    const isOtherRecipientValid =
      this.poaRecipientRoleModel.value === PoARecipientRole.other &&
      this.poaOtherRecipientModel.isValid;
    return (
      this.poaRecipientRoleModel.isValid || isShareholderRecipientValid || isOtherRecipientValid
    );
  }

  get poaRecipientName(): string {
    switch (this.poaRecipientRoleModel.value) {
      case PoARecipientRole.chairperson:
        return this.chairpersonName;
      case PoARecipientRole.ceo:
        return this.ceoName;
      case PoARecipientRole.shareholder:
        return (
          this.shareholderRecipientModel?.representative ??
          this.shareholderRecipientModel?.name ??
          ""
        );
      case PoARecipientRole.other:
        return this.poaOtherRecipientModel.name.validatedValue;
      default:
        return "";
    }
  }

  get attendanceData(): AttendanceData {
    return prepareAttendanceData(this.response);
  }

  *sendPoA() {
    this.givePoAResult = pending();
    this.validate();
    if (!this.isValid) {
      return;
    }

    const noticeToGeneralMeetingId = this.response.noticeForShareholderId;

    const poaRecipient = toPoARecipient(
      this.chairpersonName,
      this.ceoName,
      this.poaRecipientRoleModel.value,
      this.poaOtherRecipientModel,
      this.shareholderRecipientModel,
    );
    try {
      yield this.http.post(
        `/general-meeting/${this.response.projectId}/poa/${noticeToGeneralMeetingId}`,
        {
          poaRecipientRole: poaRecipient?.role,
          poaRecipientShareholderId: poaRecipient?.shareholderId,
          poaRecipientName: poaRecipient?.name,
          poaRecipientEmail: poaRecipient?.email,
        },
      );
      this.givePoAResult = ok({
        key: "noticeForShareholder.poaGiven",
        params: { name: poaRecipient?.name },
      });
      this.response.poaRecipientName = poaRecipient?.name;
      this.response.poaRecipientRole = this.poaRecipientRoleModel.value;
    } catch (error) {
      this.givePoAResult = failure({ key: "noticeForShareholder.decisions.failedToGivePoA" });
    }
  }

  *revokePoA() {
    this.givePoAResult = pending();
    const noticeToGeneralMeetingId = this.response.noticeForShareholderId;
    try {
      yield this.http.delete(
        `/general-meeting/${this.response.projectId}/poa/${noticeToGeneralMeetingId}`,
      );
      this.givePoAResult = ok({ key: "noticeForShareholder.poaRevoked" });
      this.poaRecipientRoleModel.setValue(undefined);
      this.response.poaRecipientRole = undefined;
      this.response.poaRecipientName = undefined;
      this.response.poaRecipientEmail = undefined;
      this.response.poaRecipientShareholderId = undefined;
    } catch (error) {
      this.givePoAResult = failure({ key: "noticeForShareholder.decisions.failedToRevokePoA" });
    }
  }

  openPreviewAttachmentDialog(fileId: string) {
    this.previewState = {
      type: "preview",
      filename: this.response.attachments.find((d) => d.id === fileId)?.filename ?? "",
      file: {
        url: `/api/shareholder-view/notice-general-meeting/${this.response.noticeForShareholderId}/attachment/${fileId}`,
      },
    };
  }

  closePreviewAttachmentDialog() {
    this.previewState = undefined;
  }

  downloadNoticeToGeneralMeetingAttachment(fileId: string) {
    this.downloadService
      .downloadNoticeToGeneralMeetingAttachment(this.response.noticeForShareholderId, fileId)
      .catch(() => {
        this.dispatch([showGeneralError()]);
      });
  }

  get downloadNotice(): DownloadAsPdfButtonProps {
    return {
      isDownloading: isPending(this.noticeDownloadResult),
      onConfirm: this.showDownloadNonPdfWarning ? this.downloadNoticeAndAttachments : undefined,
      onCancel: this.cancelDownloadNoticeAndAttachments,
      onDownload: this.downloadNoticeAsPDF,
      onDownloadWithAttachments:
        this.response.attachments.length > 0 ? this.downloadNoticeAndAttachments : undefined,
      documentType: DocumentType.notice_general_meeting,
    };
  }

  downloadNoticeAsPDF() {
    this.previewState = {
      type: "preview",
      filename: {
        key: "notice_toGM.filename",
        params: {
          company: this.response.companyName,
          date: this.response.meetingStartDate.split("T")[0],
        },
      },
      file: { url: this.downloadUrl },
    };
  }

  cancelDownloadNoticeAndAttachments() {
    this.showDownloadNonPdfWarning = false;
  }

  *downloadNoticeAndAttachments() {
    if (hasNonPdfAttachment(this.response.attachments) && !this.showDownloadNonPdfWarning) {
      this.showDownloadNonPdfWarning = true;
      return;
    }
    try {
      this.noticeDownloadResult = pending();
      const [file, filename]: DownloadResult = yield download(
        this.downloadNoticeAndAttachmentsUrl,
        this.http,
      );
      if (filename?.endsWith(".pdf") && file instanceof Blob) {
        this.previewState = {
          type: "preview",
          filename,
          file: { data: file },
        };
      } else {
        downloadFile(file, filename);
      }
      this.noticeDownloadResult = ok();
    } catch (e) {
      this.noticeDownloadResult = failure();
      this.dispatch([showError({ key: "archive.download-error" })]);
    } finally {
      this.showDownloadNonPdfWarning = false;
    }
  }

  setAgendaItemActionResult(result: Result<AlertMessage, AlertMessage>) {
    this.agendaItemActionResult = result;
    switch (result.state) {
      case "ok":
        this.dispatch([showAlert(result.data.message, result.data.type, result.data.params)]);
        break;
      case "failure":
        this.dispatch([showAlert(result.error.message, result.error.type, result.error.params)]);
        break;
    }
  }

  registerAttendance() {
    this.registerPresence(true);
  }

  *registerPresence(isPresent: boolean) {
    this.setAgendaItemActionResult(pending());
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/register-attendance`, {
          noticeId: this.response.noticeForShareholderId,
          participationList: this.selectedAttendance.selected.map((d) => {
            return {
              noticeId: d.noticeId,
              isPresent: isPresent,
              isCreatePowerOfAttorneyForAdditionalNotice: d.isAdditionalNotice,
            };
          }),
        })
        .then((r) => r.data);
      this.setAgendaItemActionResult(
        ok({
          type: "success",
          message: {
            key: "general-meeting.register-your-attendance.register.success",
          },
          params: { autoHide: true },
        }),
      );
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.setAgendaItemActionResult(failure({ type: "error", message: { key: "general-error" } }));
    }
  }

  *resetAttendance() {
    this.setAgendaItemActionResult(pending());
    try {
      yield this.http.post(`/general-meeting/${this.response.projectId}/reset-attendance`, {
        noticeId: this.response.noticeForShareholderId,
        noticeIdList: [
          this.response.noticeForShareholderId,
          ...this.response.receivedPowerOfAttorneys.map((poa) => {
            return poa.noticeForShareholderId;
          }),
        ],
      });
      this.setAgendaItemActionResult(
        ok({
          type: "info",
          message: {
            key: "general-meeting.register-your-attendance.reset.success",
          },
          params: { autoHide: true },
        }),
      );
      this.response.shareholderParticipation = [];
    } catch (e) {
      this.setAgendaItemActionResult(failure({ type: "error", message: { key: "general-error" } }));
    }
  }

  *onVoteForMatter({ voteType, votes, decisionId }: VotingContext, vote: IndividualVote) {
    this.setAgendaItemActionResult(pending());
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/vote`, {
          noticeId: this.response.noticeForShareholderId,
          voteList: this.selectedAttendance.selected.map((attending) => {
            return {
              noticeId: attending.noticeId,
              voteId: votes?.find((d) => d.shareholderId === attending.shareholderId)?.id,
              voteType,
              vote,
              decisionId,
            };
          }),
        })
        .then((r) => r.data);
      this.setAgendaItemActionResult(
        ok({
          type: "success",
          message: { key: "voting.you-voted", params: { vote: voteLabelUppercase(vote) } },
          params: { autoHide: true },
        }),
      );
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.setAgendaItemActionResult(failure({ type: "error", message: { key: "general-error" } }));
    }
  }

  *resetVote({ voteType, votes, decisionId }: VotingContext) {
    this.setAgendaItemActionResult(pending());
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/reset-vote`, {
          noticeId: this.response.noticeForShareholderId,
          resetVoteList: this.selectedAttendance.selected.map((attending) => {
            return {
              noticeId: attending.noticeId,
              voteType,
              voteId: votes?.find((d) => d.shareholderId === attending.shareholderId)?.id,
              decisionId,
            };
          }),
        })
        .then((r) => r.data);
      this.setAgendaItemActionResult(
        ok({
          type: "info",
          message: { key: "vote-reset-by-user" },
          params: { autoHide: true },
        }),
      );
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.setAgendaItemActionResult(failure({ type: "error", message: { key: "general-error" } }));
    }
  }

  setRequestPageReload(requestPageReload: RequestPageReload) {
    this.requestPageReload = requestPageReload;
  }
}

export type GetNoticeForShareholderResponse = {
  noticeForShareholderId: string;
  noticeDocumentContent: string;
  minutesDocumentContent?: string;
  noticeDocumentId: string;
  chairpersonName: string;
  ceoName: string;
  isCompany: boolean;
  poaRecipientName?: string;
  poaRecipientEmail?: string;
  poaRecipientRole?: PoARecipientRole;
  poaRecipientShareholderId?: number;
  attachments: UploadedAttachment[];
  companyName: string;
  orgNo: string;
  recipientName: string;
  recipientInitials: string;
  recipientEmail?: string;
  meetingStartDate: string;
  meetingEndDate: string;
  meetingLocation?: string;
  userInfo: LoggedOnUserInfoDTO;
  ical: {
    [key in Language]?: string;
  };
  googleCalendar: {
    [key in Language]?: string;
  };
  ownership: ShareOwnership[];
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  totalNumberOfShares: number;
  totalNumberOfVotes: number;
  contactPersonName?: string;
  contactInitials?: string;
  accessType: AccessType;
  shareholderId: number;
  projectId: string;
  mattersForVoting: MatterForVoting[];
  conductElectronicGeneralMeeting: boolean;
  electronicGeneralMeeting?: ElectronicGeneralMeeting;
  shareholderParticipation?: ElectronicParticipation[];
  votingEligibilityOfShares: VotingEligibilityOfShares;
  chairOfMeeting?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
  shareholders: PowerOfAttorneyShareholderOption[];
  meetingLink?: string;
  supersededBy?: string;
  noticeSentDate: string;
  ownNotices: GetNoticeForShareholderResponse[];
  shareTypes: ShareType[];
};

export type PowerOfAttorneyShareholderOption = {
  id: number;
  name: string;
  representative?: string;
  email?: string;
};

export type AttendanceOption = {
  noticeId: string;
  shareholderId: number;
  ownership: ShareOwnership[];
  label: TMessage;
  subtitle: TMessage;
  poaGivenAt?: string;
  isAdditionalNotice: boolean;
};

export enum GeneralMeetingVoteType {
  meetChair = "meetChair",
  approveNoticeAndAgenda = "approveNoticeAndAgenda",
  decision = "decision",
}

export type ElectronicParticipation = {
  isPresent?: boolean;
  shareholderId: number;
  participation?: GeneralMeetingParticipation;
  meetingChairVote?: GeneralMeetingIndividualVote;
  approveAgendaVote?: GeneralMeetingIndividualVote;
  decisionVotes: DecisionVote[];
};

export type DecisionVote = {
  decisionId: string;
  vote: GeneralMeetingIndividualVote;
};

export type VotingContext = {
  voteType: GeneralMeetingVoteType;
  decisionId?: string;
  votes: GeneralMeetingIndividualVote[];
};

export type MatterForVoting = {
  decision: Decision;
  textForReview: RenderedDecisionRow[];
  attachments: UploadedAttachment[];
  comments: CommentDTO[];
  isMultiLanguage: boolean;
};

export type ShareOwnership = {
  shareTypeId: number;
  shareTypeCode: ShareTypeCode;
  numberOfShares: number;
  vote: number;
};

export type ReceivedPowerOfAttorney = {
  noticeForShareholderId: string;
  shareholderId: number;
  name: string;
  ownership: ShareOwnership[];
  poaGivenAt?: string;
};

export type GetNoticeForPowerOfAttorneyRecipientResponse = {
  powerOfAttorneyRecipientId: string;
  noticeDocumentContent: string;
  noticeDocumentId: string;
  minutesDocumentContent?: string;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  numberOfShares: NumberOfShares[];
  companyName: string;
  orgNo: string;
  recipientName: string;
  recipientInitials: string;
  recipientEmail?: string;
  meetingStartDate: string;
  meetingEndDate: string;
  meetingLocation?: string;
  dateGeneralMeeting: string;
  timeGeneralMeeting?: string;
  ownNotices: GetNoticeForShareholderResponse[];
  ical: {
    [key in Language]: string;
  };
  googleCalendar: {
    [key in Language]?: string;
  };
  ownership: ShareOwnership[];
  attachments: UploadedAttachment[];
  totalNumberOfShares: number;
  totalNumberOfVotes: number;
  accessType: AccessType;
  shareholderId?: number;
  projectId: string;
  mattersForVoting: MatterForVoting[];
  conductElectronicGeneralMeeting: boolean;
  electronicGeneralMeeting?: ElectronicGeneralMeeting;
  shareholderParticipation?: ElectronicParticipation[];
  votingEligibilityOfShares: VotingEligibilityOfShares;
  chairOfMeeting?: CompanyRoleIdWithName;
  coSigners?: CompanyRoleIdWithName[];
  meetingLink?: string;
  poaRecipientRole?: PoARecipientRole;
  noticeSentDate: string;
  supersededByPoaRecipientId?: string;
  shareTypes: ShareType[];
};

export enum PoARecipientRole {
  chairperson = "chairperson",
  ceo = "ceo",
  shareholder = "shareholder",
  other = "other",
}

export class PowerOfAttorneyRecipientController {
  http: AxiosInstance;
  response: GetNoticeForPowerOfAttorneyRecipientResponse;
  activeView: NoticeActiveView;
  messageStore: AlertMessageStore;
  downloadService: DownloadService;
  noticeDownloadResult?: Result;
  showDownloadNonPdfWarning = false;
  ownNoticeController?: NoticeForShareholderController;
  agendaItemActionResult?: Result<AlertMessage, AlertMessage>;
  selectedAttendance: OptionFieldModel<AttendanceOption>;

  previewState?: PreviewState;

  constructor(
    response: GetNoticeForPowerOfAttorneyRecipientResponse,
    downloadService: DownloadService,
    http: AxiosInstance,
    dispatch: EventDispatcher,
    messageStore: AlertMessageStore,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.http = http;
    this.downloadService = downloadService;
    this.messageStore = messageStore;
    this.response = response;
    this.activeView = NoticeActiveView.notice;
    this.selectedAttendance = new OptionFieldModel({
      selectedIds: selectedAttendances(
        prepareAttendanceData(this.response),
        this.response.ownNotices,
      ),
      options: selectedAttendanceOptions(
        this.response.receivedPowerOfAttorneys,
        this.response.ownNotices,
      ),
      idFn: (x) => x.shareholderId.toString(),
      labelFn: (x) => x.label,
    });
    if (this.response.ownNotices.length > 0) {
      this.ownNoticeController = new NoticeForShareholderController(
        this.response.ownNotices[0],
        this.downloadService,
        this.http,
        dispatch,
        `/api/shareholder-view/notice-general-meeting/${this.response.ownNotices[0].noticeForShareholderId}/download-pdf`,
      );
    }
  }

  updateSelectedAttendances() {
    this.selectedAttendance = new OptionFieldModel({
      selectedIds: selectedAttendances(
        prepareAttendanceData(this.response),
        this.response.ownNotices,
      ),
      options: selectedAttendanceOptions(
        this.response.receivedPowerOfAttorneys,
        this.response.ownNotices,
      ),
      idFn: (x) => x.shareholderId.toString(),
      labelFn: (x) => x.label,
    });
  }

  closePreview() {
    this.previewState = undefined;
  }

  get isLiveGeneralMeeting() {
    return this.response.conductElectronicGeneralMeeting;
  }

  get givenPowerOfAttorneyTo(): string | undefined {
    return this.ownNoticeController?.givenPowerOfAttorneyTo;
  }

  downloadAttachment(attachmentId: string) {
    this.previewState = {
      type: "preview",
      filename: this.response.attachments.find((d) => d.id === attachmentId)?.filename ?? "",
      file: {
        url: `/api/shareholder-view/notice-general-meeting/poa-recipient/${this.response.powerOfAttorneyRecipientId}/attachment/${attachmentId}`,
      },
    };
  }

  get downloadNotice(): DownloadAsPdfButtonProps {
    return {
      isDownloading: isPending(this.noticeDownloadResult),
      onConfirm: this.showDownloadNonPdfWarning ? this.downloadNoticeAndAttachments : undefined,
      onCancel: this.cancelDownloadNoticeAndAttachments,
      onDownload: this.downloadNoticeAsPDF,
      onDownloadWithAttachments:
        this.response.attachments.length > 0 ? this.downloadNoticeAndAttachments : undefined,
      documentType: DocumentType.notice_general_meeting,
    };
  }

  downloadNoticeAsPDF() {
    this.previewState = {
      type: "preview",
      filename: {
        key: "notice_toGM.filename",
        params: {
          company: this.response.companyName,
          date: this.response.meetingStartDate.split("T")[0],
        },
      },
      file: {
        url: `/api/shareholder-view/notice-general-meeting/poa-recipient/${this.response.powerOfAttorneyRecipientId}/download-pdf`,
      },
    };
  }

  cancelDownloadNoticeAndAttachments() {
    this.showDownloadNonPdfWarning = false;
  }

  *downloadNoticeAndAttachments() {
    if (hasNonPdfAttachment(this.response.attachments) && !this.showDownloadNonPdfWarning) {
      this.showDownloadNonPdfWarning = true;
      return;
    }
    try {
      this.noticeDownloadResult = pending();
      const url = `/shareholder-view/notice-general-meeting/poa-recipient/${this.response.powerOfAttorneyRecipientId}/download-notice-and-attachments`;
      const [file, filename]: DownloadResult = yield download(url, this.http);
      if (filename?.endsWith(".pdf") && file instanceof Blob) {
        this.previewState = {
          type: "preview",
          filename,
          file: { data: file },
        };
      } else {
        downloadFile(file, filename);
      }
      this.noticeDownloadResult = ok();
    } catch (e) {
      this.noticeDownloadResult = failure();
      this.messageStore.showError({ key: "archive.download-error" });
    } finally {
      this.showDownloadNonPdfWarning = false;
    }
  }

  get companyName(): string {
    return this.response.companyName;
  }

  registerAttendance() {
    this.registerPresence(true);
  }

  *registerPresence(isPresent: boolean) {
    this.agendaItemActionResult = pending();
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/register-attendance`, {
          powerOfAttorneyRecipientId: this.response.powerOfAttorneyRecipientId,
          participationList: this.selectedAttendance.selected.map((d) => {
            return {
              noticeId: d.noticeId,
              isPresent: isPresent,
              isCreatePowerOfAttorneyForAdditionalNotice: d.isAdditionalNotice,
            };
          }),
        })
        .then((r) => r.data);
      this.agendaItemActionResult = ok({
        type: "success",
        message: {
          key: "general-meeting.register-your-attendance.register.success",
        },
        params: { autoHide: true },
      });
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.agendaItemActionResult = failure({ type: "error", message: { key: "general-error" } });
    }
  }

  *resetAttendance() {
    this.agendaItemActionResult = pending();
    try {
      yield this.http.post(`/general-meeting/${this.response.projectId}/reset-attendance`, {
        powerOfAttorneyRecipientId: this.response.powerOfAttorneyRecipientId,
        noticeIdList: this.selectedAttendance.selected.map((d) => d.noticeId),
      });
      this.agendaItemActionResult = ok({
        type: "info",
        message: {
          key: "general-meeting.register-your-attendance.reset.success",
        },
        params: { autoHide: true },
      });
      this.response.shareholderParticipation = [];
    } catch (e) {
      this.agendaItemActionResult = failure({ type: "error", message: { key: "general-error" } });
    }
  }

  *onVoteForMatter({ voteType, votes, decisionId }: VotingContext, vote: IndividualVote) {
    this.agendaItemActionResult = pending();
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/vote`, {
          powerOfAttorneyRecipientId: this.response.powerOfAttorneyRecipientId,
          voteList: this.selectedAttendance.selected.map((attending) => {
            return {
              noticeId: attending.noticeId,
              voteId: votes?.find((d) => d.shareholderId === attending.shareholderId)?.id,
              voteType,
              vote,
              decisionId,
            };
          }),
        })
        .then((r) => r.data);
      this.agendaItemActionResult = ok({
        type: "success",
        message: {
          key: "voting.you-voted",
          params: { vote: voteLabelUppercase(vote) },
        },
        params: { autoHide: true },
      });
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.agendaItemActionResult = failure({ type: "error", message: { key: "general-error" } });
    }
  }

  *resetVote({ voteType, votes, decisionId }: VotingContext) {
    this.agendaItemActionResult = pending();
    try {
      const response: ElectronicParticipation[] = yield this.http
        .post(`/general-meeting/${this.response.projectId}/reset-vote`, {
          powerOfAttorneyRecipientId: this.response.powerOfAttorneyRecipientId,
          resetVoteList: this.selectedAttendance.selected.map((attending) => {
            return {
              noticeId: attending.noticeId,
              voteType,
              voteId: votes?.find((d) => d.shareholderId === attending.shareholderId)?.id,
              decisionId,
            };
          }),
        })
        .then((r) => r.data);
      this.agendaItemActionResult = ok({
        type: "info",
        message: { key: "vote-reset-by-user" },
        params: { autoHide: true },
      });
      this.response.shareholderParticipation = response;
    } catch (e) {
      this.agendaItemActionResult = failure({ type: "error", message: { key: "general-error" } });
    }
  }
}

export function votingContextForMeetingChair(
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
): VotingContext {
  return {
    voteType: GeneralMeetingVoteType.meetChair,
    votes: compact(
      data.shareholderParticipation?.map((participation) => participation.meetingChairVote),
    ),
  };
}

export function votingContextForApproveAgenda(
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
): VotingContext {
  return {
    voteType: GeneralMeetingVoteType.approveNoticeAndAgenda,
    votes: compact(
      data.shareholderParticipation?.map((participation) => participation.approveAgendaVote),
    ),
  };
}

export function votingContextForDecision(
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  decisionId: string,
): VotingContext {
  return {
    voteType: GeneralMeetingVoteType.decision,
    votes: compact(
      data.shareholderParticipation?.map(
        (participation) =>
          participation.decisionVotes?.find((d) => d.decisionId === decisionId)?.vote,
      ),
    ),
    decisionId,
  };
}

export class NoticeForPowerOfAttorneyRecipientInitializerController {
  http: AxiosInstance;
  result: Result<PowerOfAttorneyRecipientController, NoticeError> = pending();
  messageStore: AlertMessageStore;
  t: TFunction;
  linkExpired = false;
  meetingPassword = new StringFieldModel();

  constructor(
    http: AxiosInstance,
    messageStore: AlertMessageStore,
    readonly dispatch: EventDispatcher,
    t: TFunction,
  ) {
    this.http = http;
    this.messageStore = messageStore;
    this.t = t;
    makeAutoObservable(this, { http: false }, { autoBind: true });
  }

  setLinkExpired() {
    this.linkExpired = true;
  }

  fetchPowerOfAttorneyRecipient(powerOfAttorneyRecipientId: string) {
    const password = this.meetingPassword.inputValue;
    this.http
      .get<GetNoticeForPowerOfAttorneyRecipientResponse>(
        `/shareholder-view/notice-general-meeting/poa-recipient/${powerOfAttorneyRecipientId}`,
        password ? { auth: { username: "", password: password } } : undefined,
      )
      .then((response) => {
        this.setResult(
          ok(
            new PowerOfAttorneyRecipientController(
              response.data,
              new DownloadService(this.http),
              this.http,
              this.dispatch,
              this.messageStore,
            ),
          ),
        );
      })
      .catch((err) => {
        if (isHttpError(HttpStatus.UNAUTHORIZED, err)) {
          const e = err.response?.data as NoticeError;
          this.setResult(
            isFailure(this.result) ? failure({ ...this.result.error, ...e }) : failure(e),
          );
          this.meetingPassword.clear();
        } else {
          this.setResult(failure({}));
        }
      });
  }

  setResult(result: Result<PowerOfAttorneyRecipientController, NoticeError>) {
    this.result = result;
  }
}

export function canGivePowerOfAttorney({
  isShareholder,
  givenPowerOfAttorneyTo,
  attendanceData,
  meetingStatus,
}: {
  isShareholder: boolean;
  givenPowerOfAttorneyTo: string | undefined;
  attendanceData: AttendanceData;
  meetingStatus: GeneralMeetingStatus | undefined;
}): boolean {
  return (
    isShareholder &&
    givenPowerOfAttorneyTo === undefined &&
    !attendanceData.isPresent &&
    !attendanceData.isFinished &&
    meetingStatus !== GeneralMeetingStatus.FINISHED
  );
}

export function createTabs({
  isShareholder,
  isRecipient,
  givenPowerOfAttorneyTo,
  activeView,
  onSelectTab,
  receivedPoAs,
  t,
  attendanceData,
  meetingStatus = GeneralMeetingStatus.PUBLISHED,
  hasMinutes = false,
  conductElectronicGeneralMeeting = false,
}: {
  isShareholder: boolean;
  isRecipient: boolean;
  givenPowerOfAttorneyTo: string | undefined;
  activeView: NoticeActiveView;
  onSelectTab: (view: NoticeActiveView) => void;
  receivedPoAs: number;
  t: TFunction;
  attendanceData: AttendanceData;
  meetingStatus?: GeneralMeetingStatus;
  hasMinutes?: boolean;
  conductElectronicGeneralMeeting?: boolean;
}) {
  const tabs: TabButtonProps[] = compact([
    {
      id: "notice",
      isActive: activeView === NoticeActiveView.notice,
      label: t("meetings.notice"),
      onSelect: () => onSelectTab(NoticeActiveView.notice),
    },
    isRecipient && {
      id: "recepient_poa",
      isActive: activeView === NoticeActiveView.receivedPoA,
      label: t("poa_received", { number: receivedPoAs }),
      onSelect: () => onSelectTab(NoticeActiveView.receivedPoA),
    },
    isShareholder &&
      givenPowerOfAttorneyTo !== undefined && {
        id: "poa",
        isActive: activeView === NoticeActiveView.poa,
        label: t("poa_to", { name: givenPowerOfAttorneyTo }),
        onSelect: () => onSelectTab(NoticeActiveView.poa),
      },
    canGivePowerOfAttorney({
      isShareholder,
      givenPowerOfAttorneyTo,
      attendanceData,
      meetingStatus,
    }) && {
      id: "poa",
      isActive: activeView === NoticeActiveView.poa,
      label: t("noticeForShareholder.givePoAButton"),
      onSelect: () => onSelectTab(NoticeActiveView.poa),
    },
    conductElectronicGeneralMeeting && {
      id: NoticeActiveView.meeting,
      isActive: activeView === NoticeActiveView.meeting,
      label: t("shareholder-view.conduct-general-meeting"),
      onSelect: () => onSelectTab(NoticeActiveView.meeting),
    },
    hasMinutes && {
      id: "minutes",
      isActive: activeView === NoticeActiveView.minutes,
      label: t("meetings.minutes"),
      onSelect: () => onSelectTab(NoticeActiveView.minutes),
    },
  ]);
  return tabs;
}

export function votesForOwnership(ownership: ShareOwnership[]) {
  return ownership
    .reduce((sum, current) => sum.plus(Big(current.vote).mul(current.numberOfShares)), Big(0))
    .toNumber();
}

export function relativeVotes(ownership: ShareOwnership[], totalNumberOfVotes: number): number {
  return percentOf(votesForOwnership(ownership), totalNumberOfVotes);
}

export function sumForOwnership(ownership: ShareOwnership[]) {
  return ownership.reduce((sum, current) => sum + current.numberOfShares, 0);
}

export function relativeOwnership(
  ownership: ShareOwnership[],
  totalNumberOfShares: number,
): number {
  return percentOf(sumForOwnership(ownership), totalNumberOfShares);
}

export type DiagramData = {
  title: TMessage[];
  subtitle: TMessage;
  infoRows?: (TMessage | TMessageParam)[][];
  data: PieChartDatum[];
};

export function createDiagramDataForNotice(
  ownership: ShareOwnership[],
  noticeSentDate: string,
  total: number,
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[],
  formatPercent: (n: number) => string,
  lang: Language,
  titleHeader?: TMessage,
): DiagramData {
  let myTotal = votesForOwnership(ownership);
  if (receivedPowerOfAttorneys.length > 0) {
    myTotal += receivedPowerOfAttorneys.reduce(
      (sum, current) => sum + votesForOwnership(current.ownership),
      0,
    );
  }

  const infoRows: (TMessage | TMessageParam)[][] = compact([
    receivedPowerOfAttorneys.length === 1 && [{ key: "including-poas-singular" }],
    receivedPowerOfAttorneys.length > 1 && [
      { key: "including-poas-plural", params: { n: receivedPowerOfAttorneys.length } },
    ],
    [{ key: "votes" }, { type: "number", value: myTotal }],
    [{ key: "of-total" }, { type: "number", value: total }],
  ]);

  return {
    title: [
      titleHeader ?? { key: "share-of-votes-in-company" },
      { translated: formatPercent(percentOf(myTotal, total)) },
    ],
    subtitle: { key: "per-date", params: { date: formatDateTime(lang, noticeSentDate) } },
    infoRows: infoRows,
    data: [
      { value: myTotal, label: { key: "votes", params: { myTotal: myTotal } } },
      {
        value: total - myTotal,
        label: { key: "the-rest", params: { total: total } },
        isTotal: true,
      },
    ],
  };
}

export type AttendanceData = {
  isPresent: boolean;
  isStarted: boolean;
  isFinished: boolean;
  recipientName: string;
  totalNumberOfVotes: number;
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[];
  ownership: ShareOwnership[];
  shareholderParticipation: ElectronicParticipation[];
  recipientShareholderId?: number;
};

export const getRegistrationStatus = (
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
): GeneralMeetingStatus =>
  data.electronicGeneralMeeting?.registrationStatus ?? GeneralMeetingStatus.PUBLISHED;

export const prepareAttendanceData = (
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
): AttendanceData => ({
  isPresent: data.shareholderParticipation
    ? data.shareholderParticipation.some((participation) => participation.isPresent)
    : false,
  isStarted: getRegistrationStatus(data) === GeneralMeetingStatus.STARTED,
  isFinished: getRegistrationStatus(data) === GeneralMeetingStatus.FINISHED,
  recipientName: data.recipientName,
  totalNumberOfVotes: data.totalNumberOfVotes,
  receivedPowerOfAttorneys: data.receivedPowerOfAttorneys,
  ownership: data.ownership,
  shareholderParticipation: data.shareholderParticipation ?? [],
  recipientShareholderId: data.shareholderId,
});

export function hasVoted(
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
) {
  return (
    data.shareholderParticipation?.some(
      (s) =>
        s.meetingChairVote !== undefined ||
        s.approveAgendaVote !== undefined ||
        s.decisionVotes.length > 0,
    ) ?? false
  );
}

export type MatterForVotingData = {
  matter: MatterForVoting;
  status: GeneralMeetingStatus;
  votingContext: VotingContext;
};

export const prepareMatterForVotingData = ({
  data,
  matter,
}: {
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  matter: MatterForVoting;
}): MatterForVotingData => ({
  matter,
  status:
    data.electronicGeneralMeeting?.decisionStatuses.find(
      (s) => s.decisionId === matter.decision.decisionId,
    )?.status ?? GeneralMeetingStatus.PUBLISHED,
  votingContext: votingContextForDecision(data, matter.decision.decisionId),
});

export const prepareApproveAgendaVotingData = ({
  data,
  t,
  language,
}: {
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  t: TFunction;
  language: Language;
}): MatterForVotingData => ({
  matter: {
    decision: {
      decisionId: "approveAgenda",
      decisionData: {
        ...emptyFreetext,
        localizedTitles: [{ language, text: t("documents.generalMeeting.approveAgenda") }],
      },
      decisionTexts: [],
      decisionIntroTexts: [],
      attachments: [],
    },
    textForReview: [{ cells: [t("general-meeting.approveAgenda.text")] }],
    attachments: [],
    comments: [],
    isMultiLanguage: false,
  },
  votingContext: votingContextForApproveAgenda(data),
  status: data.electronicGeneralMeeting?.approveAgendaStatus ?? GeneralMeetingStatus.PUBLISHED,
});

export const prepareMeetingChairVotingData = ({
  data,
  t,
  language,
}: {
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse;
  t: TFunction;
  language: Language;
}): MatterForVotingData => ({
  matter: {
    decision: {
      decisionId: "meetingChair",
      decisionData: {
        ...emptyFreetext,
        localizedTitles: [{ language, text: t("documents.generalMeeting.choiceOfMeetingChair") }],
      },
      decisionTexts: [],
      decisionIntroTexts: [],
      attachments: [],
    },
    textForReview: [
      {
        cells: compact<string>([
          t("general-meeting.chairOfMeeting.text", { name: data.chairOfMeeting?.name }),
          data.coSigners && data.coSigners.length > 0
            ? t("general-meeting.coSigners.text", {
                coSigners: { type: "list", value: data.coSigners.map((s) => s.name) },
                count: data.coSigners.length,
              })
            : undefined,
        ]),
      },
    ],
    attachments: [],
    comments: [],
    isMultiLanguage: false,
  },
  status: data.electronicGeneralMeeting?.meetingChairStatus ?? GeneralMeetingStatus.PUBLISHED,
  votingContext: votingContextForMeetingChair(data),
});

export const meetingUrl = (
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  view: NoticeActiveView = NoticeActiveView.notice,
  { isAdminPreview, item }: { isAdminPreview?: boolean; item?: AgendaItemType } = {},
): string => {
  const base = isAdminPreview
    ? `/company/${data.orgNo}/general-meeting-preview/${data.projectId}`
    : "noticeForShareholderId" in data
      ? `/notice-general-meeting/${data.noticeForShareholderId}`
      : `/notice-general-meeting-poa-recipient/${data.powerOfAttorneyRecipientId}`;

  const itemPart = item?.type === "matter" ? item.matter.decision.decisionId : item?.type;

  return compact([base, view, itemPart]).join("/");
};

export const meetingUrlState = (isAdminPreview: boolean, history: History) =>
  isAdminPreview ? history.location.state : undefined;

export function mapToAgendaItem(
  data: GetNoticeForShareholderResponse | GetNoticeForPowerOfAttorneyRecipientResponse,
  matterParam: AgendaItemType["type"] | string | undefined,
): AgendaItemType | undefined {
  switch (matterParam) {
    case "":
    case undefined:
      return undefined;
    case "attendance":
      return { type: "attendance" };
    case "meetingChair":
      return { type: "meetingChair" };
    case "approveAgenda":
      return { type: "approveAgenda" };
    default: {
      const matter = data.mattersForVoting.find((m) => m.decision.decisionId === matterParam);
      return matter
        ? {
            type: "matter",
            matter,
          }
        : undefined;
    }
  }
}

export function toPoARecipient(
  chairperson: string,
  ceo: string,
  role: PoARecipientRole | undefined,
  other: PoAOtherRecipientModel,
  shareholder?: PowerOfAttorneyShareholderOption,
): { role: PoARecipientRole; name?: string; email?: string; shareholderId?: number } | undefined {
  switch (role) {
    case PoARecipientRole.chairperson:
      return { role, name: chairperson };
    case PoARecipientRole.ceo:
      return { role, name: ceo };
    case PoARecipientRole.shareholder:
      return {
        role,
        name: shareholder?.representative ?? shareholder?.name,
        shareholderId: shareholder?.id,
      };
    case PoARecipientRole.other:
      return other.selectedSuggestion
        ? {
            role: PoARecipientRole.shareholder,
            name: other.selectedSuggestion.representative ?? other.selectedSuggestion.name,
            shareholderId: other.selectedSuggestion.id,
          }
        : {
            role,
            name: other.name.validatedValue,
            email: other.email.validatedValue,
          };
    default:
      return undefined;
  }
}

export const getAgendaItems = (data: { mattersForVoting: MatterForVoting[] }): AgendaItemType[] => [
  { type: "attendance" },
  { type: "meetingChair" },
  { type: "approveAgenda" },
  ...data.mattersForVoting.map<AgendaItemType>((matter) => ({ type: "matter", matter })),
];

const indexOfAgendaItem = (items: AgendaItemType[], item?: AgendaItemType) =>
  items.findIndex((x) =>
    x.type === "matter" && item?.type === "matter"
      ? x.matter.decision.decisionId === item.matter.decision.decisionId
      : x.type === item?.type,
  );

export const nextAgendaItem = (
  items: AgendaItemType[],
  current: AgendaItemType | undefined,
): AgendaItemType | undefined => items[indexOfAgendaItem(items, current) + 1];

export const prevAgendaItem = (
  items: AgendaItemType[],
  current: AgendaItemType | undefined,
): AgendaItemType | undefined => items[indexOfAgendaItem(items, current) - 1];

export function mapReceivedPowerOfAttorneyToAttendanceOption(
  receivedPowerOfAttorney: ReceivedPowerOfAttorney,
): AttendanceOption {
  return {
    noticeId: receivedPowerOfAttorney.noticeForShareholderId,
    shareholderId: receivedPowerOfAttorney.shareholderId,
    ownership: receivedPowerOfAttorney.ownership,
    label: { translated: receivedPowerOfAttorney.name },
    subtitle: {
      key: "general-meeting.attendance.type.withPoA",
      params: { date: { type: "date", value: receivedPowerOfAttorney.poaGivenAt } },
    },
    poaGivenAt: receivedPowerOfAttorney.poaGivenAt,
    isAdditionalNotice: false,
  };
}

export function selectedAttendances(
  attendance: AttendanceData,
  otherNotices: GetNoticeForShareholderResponse[],
  ownNotice?: GetNoticeForShareholderResponse,
): string[] {
  if (attendance.isPresent) {
    return compact(
      attendance.shareholderParticipation
        .filter((d) => d.isPresent)
        .map((d) => d.shareholderId.toString()),
    );
  } else {
    return selectedAttendanceOptions(
      attendance.receivedPowerOfAttorneys,
      otherNotices,
      ownNotice,
    ).map((attending) => attending.shareholderId.toString());
  }
}

export function selectedAttendanceOptions(
  receivedPowerOfAttorneys: ReceivedPowerOfAttorney[],
  otherNotices: GetNoticeForShareholderResponse[],
  ownNotice?: GetNoticeForShareholderResponse,
): AttendanceOption[] {
  const otherAttendances: AttendanceOption[] = otherNotices.map((notice) => {
    return {
      noticeId: notice.noticeForShareholderId,
      shareholderId: notice.shareholderId,
      ownership: notice.ownership,
      label: { translated: notice.recipientName },
      subtitle: {
        key: notice.isCompany
          ? "general-meeting.attendance.type.representative"
          : "general-meeting.attendance.type.personal",
      },
      isAdditionalNotice: true,
    };
  });
  return compact([
    ownNotice && {
      noticeId: ownNotice.noticeForShareholderId,
      shareholderId: ownNotice.shareholderId,
      ownership: ownNotice.ownership,
      label: { translated: ownNotice.recipientName },
      subtitle: {
        key: ownNotice.isCompany
          ? "general-meeting.attendance.type.representative"
          : "general-meeting.attendance.type.personal",
      },
      isAdditionalNotice: false,
    },
    ...otherAttendances,
    ...receivedPowerOfAttorneys.map((d) => mapReceivedPowerOfAttorneyToAttendanceOption(d)),
  ]);
}

export function isProceduralMatter(votingContext: VotingContext) {
  return (
    votingContext.voteType === GeneralMeetingVoteType.approveNoticeAndAgenda ||
    votingContext.voteType === GeneralMeetingVoteType.meetChair
  );
}

export function decisionLabel(status?: GeneralMeetingStatus) {
  switch (status) {
    case GeneralMeetingStatus.STARTED:
      return "stop-voting";
    case GeneralMeetingStatus.FINISHED:
      return "reopen-voting";
    default:
      return "start-voting";
  }
}

export function informationMatterLabel(status?: GeneralMeetingStatus): TMessage {
  switch (status) {
    case GeneralMeetingStatus.STARTED:
      return { key: "general-meeting.information-matter.close-matter" };
    case GeneralMeetingStatus.FINISHED:
      return { key: "general-meeting.information-matter.reopen-matter" };
    default:
      return { key: "general-meeting.information-matter.open-matter" };
  }
}

export function registrationLabel(status?: GeneralMeetingStatus) {
  switch (status) {
    case GeneralMeetingStatus.STARTED:
      return "general-meeting.register.stop";
    case GeneralMeetingStatus.FINISHED:
      return "general-meeting.register.restart";
    default:
      return "general-meeting.register.start";
  }
}
